import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Wallet,
  Smartphone,
  Banknote,
  ShieldCheck,
} from "lucide-react";

const STEPS = [
  { key: "profile", label: "Merchant profile" },
  { key: "kfs", label: "KFS" },
  { key: "cover", label: "Cover options" },
  { key: "payment", label: "Payment" },
  { key: "dashboard", label: "Dashboard" },
];

function ProgressIndicator({ active }) {
  const activeIdx = STEPS.findIndex((s) => s.key === active);
  return (
    <ol className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1" data-testid="payment-progress">
      {STEPS.map((s, i) => {
        const done = i < activeIdx;
        const current = i === activeIdx;
        return (
          <li key={s.key} className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span
              className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold transition-colors ${
                done
                  ? "bg-[#2d7a4a] text-white"
                  : current
                    ? "bg-[#114232] text-[#f4f2eb]"
                    : "bg-[#114232]/8 text-[#114232]"
              }`}
            >
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span
              className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${
                current ? "text-[#114232]" : done ? "text-[#5c6360]" : "text-[#5c6360]/70"
              }`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-[#114232]/15" />}
          </li>
        );
      })}
    </ol>
  );
}

function formatKES(value) {
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(value);
}

const TIER_LABELS = {
  merchant_lite: "Starter",
  social_vendor_pro: "Growth",
  enterprise_sme: "Business",
};

function tierLabel(code) {
  return TIER_LABELS[code] || code;
}

const METHODS = [
  {
    code: "mpesa",
    label: "M-Pesa STK Push",
    icon: Smartphone,
    desc: "Receive a prompt on your phone to approve the weekly premium. Recommended.",
  },
  {
    code: "capitalpay",
    label: "CapitalPay card",
    icon: Wallet,
    desc: "Visa or Mastercard via CapitalPay's secure checkout.",
  },
  {
    code: "manual",
    label: "Manual reference",
    icon: Banknote,
    desc: "Already paid? Enter the M-Pesa or bank reference to confirm.",
  },
];

export default function PaymentPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const policyId = params.get("policy_id");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [policy, setPolicy] = useState(null);
  const [method, setMethod] = useState("mpesa");
  const [manualRef, setManualRef] = useState("");
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/merchant/cover-options");
      const target =
        (policyId &&
          (r.data.current_policy?.id === policyId ? r.data.current_policy : null)) ||
        r.data.current_policy;
      if (!target) {
        setError(
          "No policy ready for payment. Please pick a cover tier first.",
        );
      } else {
        setPolicy(target);
        if (target.status === "active" || target.status === "cooling_off") {
          setActivated(true);
        }
      }
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(detail);
      if (e.response?.status === 401) {
        navigate("/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, policyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function activate() {
    if (!policy) return;
    if (method === "manual" && !manualRef.trim()) {
      toast.error("Please enter the payment reference.");
      return;
    }
    setActivating(true);
    try {
      const r = await api.post("/merchant/cover-options/activate", {
        policy_id: policy.id,
        payment_method: method,
        manual_payment_reference: method === "manual" ? manualRef.trim() : null,
      });
      setPolicy(r.data.policy);
      setActivated(true);
      toast.success(r.data.already_active ? "Already active" : "Cover activated 🎉");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActivating(false);
    }
  }

  return (
    <div
      className="px-5 sm:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8 max-w-4xl mx-auto"
      data-testid="payment-page"
    >
      <Toaster richColors position="top-right" />

      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#c15337]">
          Payment
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#114232] mt-1.5 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8" /> Activate your cover
        </h1>
        <p className="text-sm sm:text-base text-[#5c6360] mt-2 max-w-2xl leading-relaxed">
          One last step. Confirm your first weekly premium to start cover. You'll have a 7-day
          cooling-off period before claims are eligible.
        </p>
      </div>

      <ProgressIndicator active={activated ? "dashboard" : "payment"} />

      {loading ? (
        <div className="rounded-2xl bg-white border border-[#114232]/10 p-12 grid place-items-center text-[#5c6360]" data-testid="payment-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-2xl bg-[#c15337]/8 border border-[#c15337]/25 p-5 flex items-start justify-between gap-4" data-testid="payment-error">
          <div className="text-sm text-[#114232]">
            <strong>{error}</strong>
          </div>
          <Link
            to="/merchant/cover-options"
            data-testid="payment-back-to-cover"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#114232]/20 rounded-full bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
          >
            Pick a tier
          </Link>
        </div>
      ) : activated ? (
        <div
          className="rounded-3xl bg-[#2d7a4a]/8 border border-[#2d7a4a]/30 p-7 sm:p-10 space-y-5"
          data-testid="payment-activated"
        >
          <div className="flex items-start gap-4">
            <CheckCircle2 className="w-7 h-7 text-[#2d7a4a] shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232]">
                Cover is active 🎉
              </div>
              <div className="text-sm text-[#5c6360] mt-1.5">
                You're now protected. Cooling-off ends on{" "}
                <strong className="text-[#114232]">
                  {policy.cooling_off_end_date
                    ? new Date(policy.cooling_off_end_date).toLocaleDateString("en-KE")
                    : "—"}
                </strong>{" "}
                — claims filed after that date are fully eligible.
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              ["Tier", policy.tier],
              ["Weekly premium", formatKES(policy.premium_amount)],
              ["Cover limit", formatKES(policy.cover_limit)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl bg-white border border-[#114232]/10 p-4">
                <div className="text-[10px] uppercase tracking-wider text-[#5c6360] font-bold">
                  {label}
                </div>
                <div className="font-heading font-bold text-lg text-[#114232] mt-1 metric-value">
                  {value}
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/merchant"
            data-testid="payment-go-dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] text-sm font-semibold transition-all shadow-md hover:shadow-lg"
          >
            Go to dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Order summary */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl bg-white border border-[#114232]/10 p-6 sticky top-6" data-testid="payment-summary">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#c15337] font-bold mb-2">
                Order summary
              </div>
              <div className="font-heading font-bold text-lg text-[#114232] mb-4">
                {tierLabel(policy.tier)}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Weekly premium</span>
                  <span className="font-semibold text-[#114232] metric-value">
                    {formatKES(policy.premium_amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Cover limit</span>
                  <span className="font-semibold text-[#114232] metric-value">
                    {formatKES(policy.cover_limit)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Currency</span>
                  <span className="font-semibold text-[#114232]">KES</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#114232]/10 text-xs text-[#5c6360]">
                Billed weekly. Seven-day cooling-off applies on activation.
              </div>
            </div>
          </div>

          {/* Method selection */}
          <div className="lg:col-span-2 space-y-3">
            {METHODS.map(({ code, label, icon: Icon, desc }) => (
              <button
                key={code}
                type="button"
                onClick={() => setMethod(code)}
                data-testid={`payment-method-${code}`}
                className={`w-full text-left rounded-2xl border p-5 flex items-start gap-4 transition-all ${
                  method === code
                    ? "bg-white border-[#114232] shadow-md"
                    : "bg-white border-[#114232]/10 hover:border-[#114232]/30"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
                    method === code ? "bg-[#114232] text-[#f4f2eb]" : "bg-[#114232]/8 text-[#114232]"
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.7} />
                </div>
                <div className="flex-1">
                  <div className="font-heading font-bold text-[#114232]">{label}</div>
                  <div className="text-sm text-[#5c6360] mt-0.5">{desc}</div>
                </div>
                <span
                  className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 mt-1 transition-colors ${
                    method === code ? "border-[#c15337] bg-[#c15337]" : "border-[#114232]/20"
                  }`}
                >
                  {method === code && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </span>
              </button>
            ))}

            {method === "manual" && (
              <input
                type="text"
                value={manualRef}
                onChange={(e) => setManualRef(e.target.value)}
                placeholder="M-Pesa or bank reference (e.g. QXY12AB3CD)"
                data-testid="payment-manual-ref"
                className="w-full px-4 py-2.5 rounded-xl border border-[#114232]/15 bg-white text-sm text-[#1b1d1c] focus:outline-none focus:border-[#114232]/40 transition-colors"
              />
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 pt-3">
              <Link
                to="/merchant/cover-options"
                data-testid="payment-back"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-[#5c6360] hover:text-[#114232] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to cover options
              </Link>
              <div className="flex-1" />
              <button
                onClick={activate}
                disabled={activating}
                data-testid="payment-activate"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#c15337] hover:bg-[#a8472f] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all shadow-md hover:shadow-lg"
              >
                {activating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Activating…
                  </>
                ) : (
                  <>
                    Pay {formatKES(policy.premium_amount)} & activate <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Check({ className, strokeWidth }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
